import { useState, useRef, useEffect } from 'react';
import { Sparkles, Plus, X, Calendar, ChevronDown, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Calendar as CalendarPicker } from './ui/calendar';
import { WzSearch } from './WzSearch';
import { DateTextInput as SharedDateTextInput, isValidIsoDate } from './DateTextInput';
import RichTextEditor from './RichTextEditor';
import { refineItem } from '../lib/generationClient';

/**
 * Parses various date formats and returns YYYY-MM-DD (for internal storage)
 * Supports: dd.mm.yyyy, dd/mm/yyyy, dd-mm-yyyy, yyyy-mm-dd
 */
function parseDateInput(input: string): string {
  const trimmed = input.trim();
  // Already ISO format
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  // dd.mm.yyyy or dd/mm/yyyy or dd-mm-yyyy
  const match = trimmed.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (match) {
    const [, day, month, year] = match;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return trimmed;
}

/** Formats YYYY-MM-DD to dd.mm.yyyy for display */
function formatDateDisplay(isoDate: string): string {
  if (!isoDate) return '';
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    return `${match[3]}.${match[2]}.${match[1]}`;
  }
  return isoDate;
}

/** Combined text + date-picker input: allows paste/type AND calendar picker */
function DateTextInput({ value, onChange, placeholder, darkMode }: {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  darkMode: boolean;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Während des Tippens NICHT bei jedem Tastendruck neu formatieren/padden — das ließ den
  // Cursor springen und einzelne Ziffern beim Ändern einer Stelle "verschwinden"/auf 0 springen
  // (Kundenfeedback 16.10.2026, siehe auch DateTextInput.tsx). Rohtext bleibt während des
  // Fokus frei editierbar, erst beim Verlassen des Felds wird geparst/formatiert/committed.
  const [draft, setDraft] = useState<string | null>(null);
  const displayValue = draft !== null ? draft : formatDateDisplay(value);
  const selectedDate = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(value + 'T12:00:00') : undefined;

  // Schließe Kalender, wenn außerhalb geklickt wird
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <input
        translate="no"
        type="text"
        value={displayValue}
        onFocus={() => setDraft(formatDateDisplay(value))}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => {
          const raw = e.target.value.trim();
          if (raw) {
            const parsed = parseDateInput(raw);
            if (isValidIsoDate(parsed)) {
              onChange(parsed);
            }
          } else {
            onChange('');
          }
          setDraft(null);
        }}
        placeholder={placeholder || 'tt.mm.jjjj'}
        className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
          darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'
        }`}
      />
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${
          darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'
        }`}
      >
        <Calendar className="w-4 h-4" />
      </button>
      {open && (
        <div
          className={`absolute z-50 mt-1 rounded-lg border shadow-lg ${
            darkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200'
          }`}
          style={{ top: '100%', left: 0 }}
        >
            <CalendarPicker
              mode="single"
              selected={selectedDate}
              onDayClick={(day) => {
                const iso = day.toISOString().split('T')[0];
                onChange(iso);
                setDraft(null);
                setOpen(false);
              }}
            />
        </div>
      )}
    </div>
  );
}

interface Section1CardsProps {
  darkMode: boolean;
  cards: Array<{
    id: string;
    title: string;
    fields: Array<{
      id: string;
      label: string;
      placeholder?: string;
      type: 'text' | 'textarea' | 'email' | 'number' | 'date' | 'date-text' | 'select' | 'wz-search' | 'divider';
      options?: string[];
      rows?: number;
      fullWidth?: boolean;
      defaultValue?: string;
      groupLabel?: string;
      groupWith?: string;
    }>;
  }>;
  formData: Record<string, string>;
  onFieldChange: (fieldId: string, value: string) => void;
  cardTexts: Record<string, { aiDraft: string; finalText: string; showAiDraft: boolean; saved: boolean }>;
  onUpdateCardText: (cardId: string, field: 'aiDraft' | 'finalText' | 'showAiDraft' | 'saved', value: any) => void;
  onGenerateCard: (cardId: string) => void;
  onSaveCard: (cardId: string) => void;
  consultingDays?: Array<{ date: string; hours: string }>;
  onAddConsultingDay?: () => void;
  onRemoveConsultingDay?: (index: number) => void;
  onUpdateConsultingDay?: (index: number, field: 'date' | 'hours', value: string) => void;
  consultants?: Array<{ name: string }>;
  onAddConsultant?: () => void;
  onRemoveConsultant?: (index: number) => void;
  onUpdateConsultant?: (index: number, value: string) => void;
  externalDayBookings?: Array<{ date: string; reportId: number; berichtstitel: string; kundeName: string }>;
  collapsedCards?: Record<string, boolean>;
  onToggleCard?: (id: string) => void;
  onCardComplete?: (cardId: string) => void;
  isCardComplete?: (cardId: string) => boolean;
}

export function Section1Cards({
  darkMode,
  cards,
  formData,
  onFieldChange,
  cardTexts,
  onUpdateCardText,
  onGenerateCard,
  onSaveCard,
  consultingDays,
  onAddConsultingDay,
  onRemoveConsultingDay,
  onUpdateConsultingDay,
  consultants,
  onAddConsultant,
  onRemoveConsultant,
  onUpdateConsultant,
  externalDayBookings,
  collapsedCards: collapsedCardsProp,
  onToggleCard,
  onCardComplete,
  isCardComplete,
}: Section1CardsProps) {
  const [collapsedCardsLocal, setCollapsedCardsLocal] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    cards.forEach((card) => { initial[card.id] = true; });
    return initial;
  });
  const collapsedCards = collapsedCardsProp ?? collapsedCardsLocal;
  const toggleCard = (id: string) => {
    if (onToggleCard) onToggleCard(id);
    else setCollapsedCardsLocal(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {cards.map((card) => {
        const cardState = cardTexts[card.id] || { aiDraft: '', finalText: '', showAiDraft: false, saved: false };
        const isCollapsed = !!collapsedCards[card.id];

        // Feature A: Beratungstage gegen das Beratungszeitraum-Fenster [startDate … endDate] prüfen.
        // ISO-Strings (yyyy-mm-dd) lassen sich lexikografisch vergleichen; nur valide Grenzen anwenden.
        const isISODate = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);
        const fmtDE = (iso: string) => {
          const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
          return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
        };
        const winStart = isISODate(formData['startDate'] || '') ? formData['startDate'] : '';
        const winEnd = isISODate(formData['endDate'] || '') ? formData['endDate'] : '';
        const dayWindowIssues =
          card.id === 'card-1-5'
            ? (consultingDays || [])
                .map((d, i) => {
                  if (!isISODate(d.date)) return null;
                  if (winStart && d.date < winStart) return { i, date: d.date, kind: 'before' as const };
                  if (winEnd && d.date > winEnd) return { i, date: d.date, kind: 'after' as const };
                  return null;
                })
                .filter((x): x is { i: number; date: string; kind: 'before' | 'after' } => x !== null)
            : [];
        const dayIssueIdx = new Set(dayWindowIssues.map((x) => x.i));
        const hasWindowIssues = dayWindowIssues.length > 0;

        // Feature C: Überschneidung mit anderen Berichten (datum-basiert, nicht-blockierend).
        const bookingsByDate = new Map<string, Array<{ berichtstitel: string; kundeName: string }>>();
        if (card.id === 'card-1-5') {
          for (const b of externalDayBookings || []) {
            const list = bookingsByDate.get(b.date);
            if (list) list.push({ berichtstitel: b.berichtstitel, kundeName: b.kundeName });
            else bookingsByDate.set(b.date, [{ berichtstitel: b.berichtstitel, kundeName: b.kundeName }]);
          }
        }
        const dayConflicts =
          card.id === 'card-1-5'
            ? (consultingDays || [])
                .map((d, i) => {
                  const matches = isISODate(d.date) ? bookingsByDate.get(d.date) : undefined;
                  return matches && matches.length > 0 ? { i, date: d.date, matches } : null;
                })
                .filter(
                  (x): x is { i: number; date: string; matches: Array<{ berichtstitel: string; kundeName: string }> } =>
                    x !== null,
                )
            : [];
        const dayConflictIdx = new Set(dayConflicts.map((x) => x.i));
        // Ganze Karte 1.5 als Warnung markieren, sobald eine Terminüberschneidung
        // vorliegt — auch im zugeklappten Zustand sichtbar (gelber Rahmen + Badge).
        const hasDayConflict = card.id === 'card-1-5' && dayConflicts.length > 0;

        return (
          <div
            key={card.id}
            data-card-id={card.id}
            className={`rounded-lg border shadow-sm scroll-mt-[70px] ${
              hasDayConflict
                ? darkMode ? 'bg-amber-900/10 border-amber-700' : 'bg-amber-50 border-amber-300'
                : darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'
            }`}
            onBlur={(e) => {
              // Only fire when focus leaves the card entirely
              if (e.currentTarget.contains(e.relatedTarget as Node)) return;
              // User navigating to a specific other card/section — don't advance.
              const r = e.relatedTarget as HTMLElement | null;
              if (r && (r.closest('[data-card-id]') || r.closest('[data-section-id]'))) return;
              // relatedTarget is null for many in-card mouse interactions
              // (e.g. a plain <button> click on WebKit/macOS doesn't move
              // keyboard focus there). Re-check activeElement a tick later
              // instead of trusting the synchronous relatedTarget.
              const container = e.currentTarget;
              setTimeout(() => {
                if (container.contains(document.activeElement)) return;
                onCardComplete?.(card.id);
              }, 0);
            }}
          >
            {/* Card Header — clickable */}
            <div
              className={`flex items-center justify-between px-6 md:px-8 py-4 cursor-pointer select-none transition-colors rounded-lg ${
                isCollapsed ? '' : 'rounded-b-none'
              } ${darkMode ? 'hover:bg-slate-700/60' : 'hover:bg-slate-50'}`}
              onClick={() => toggleCard(card.id)}
            >
              <h3 className={`text-base md:text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {card.title}
              </h3>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                {hasDayConflict && (
                  <span
                    title={`Terminüberschneidung mit ${dayConflicts.length} ${dayConflicts.length === 1 ? 'Beratungstag' : 'Beratungstagen'} aus anderen Berichten`}
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[12px] font-medium ${darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700'}`}
                  >
                    <AlertTriangle className="w-3 h-3" />
                    Überschneidung
                  </span>
                )}
                {isCollapsed && isCardComplete?.(card.id) && (
                  <span
                    title="Inhalt vorhanden"
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[12px] font-medium ${darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    Inhalt
                  </span>
                )}
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''} ${darkMode ? 'text-slate-400' : 'text-slate-400'}`} />
              </div>
            </div>

            {/* Collapsible card content */}
            <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isCollapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'}`}>
            <div className="overflow-hidden">
            <div className="px-6 md:px-8 pb-6 md:pb-8 pt-2">
            {/* Two-Column Layout for this card */}
            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              {/* LEFT: Input Fields */}
              <div className="flex-1 lg:w-[50%]">
                <div className={`rounded-lg p-5 ${
                  darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50/50 border-slate-200'
                } border`}>
                  {/* Special layout for card 1.2 Angaben zum Unternehmen */}
                  {card.id === 'card-1-2' ? (
                    <div className="space-y-4">
                      {/* Ansprechperson Divider */}
                      {(() => {
                        const field = card.fields.find(f => f.type === 'divider');
                        if (!field) return null;
                        return (
                          <div className={`pb-1 ${darkMode ? '' : ''}`}>
                            <span className={`text-xs font-semibold tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              {field.label.toUpperCase()}
                            </span>
                          </div>
                        );
                      })()}

                      {/* Anrede (20%) + Vorname (40%) + Nachname (40%) - one row */}
                      <div className="flex gap-3">
                        {['Anrede', 'Vorname', 'Nachname'].map((label, idx) => {
                          const field = card.fields.find(f => f.label === label);
                          if (!field) return null;
                          const widthClass = idx === 0 ? 'w-[20%]' : 'w-[40%]';
                          return (
                            <div key={field.id} className={widthClass}>
                              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                {field.label}
                              </label>
                              {field.type === 'select' ? (
                                <select
                                  translate="no"                                  value={formData[field.id] || ''}
                                  onChange={(e) => onFieldChange(field.id, e.target.value)}
                                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white' : 'bg-white border-slate-300'}`}
                                >
                                  <option value="">–</option>
                                  {field.options?.map((option) => (
                                    <option key={option} value={option}>{option}</option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  translate="no"                                  value={formData[field.id] || field.defaultValue || ''}
                                  onChange={(e) => onFieldChange(field.id, e.target.value)}
                                  type={field.type}
                                  placeholder={field.placeholder}
                                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'}`}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Telefon (50%) + E-Mail (50%) - side by side */}
                      <div className="flex gap-3">
                        {['Telefon', 'E-Mail'].map((label) => {
                          const field = card.fields.find(f => f.label === label);
                          if (!field) return null;
                          return (
                            <div key={field.id} className="w-1/2">
                              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                {field.label}
                              </label>
                              <input
                                translate="no"                                value={formData[field.id] || field.defaultValue || ''}
                                onChange={(e) => onFieldChange(field.id, e.target.value)}
                                type={field.type}
                                placeholder={field.placeholder}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'}`}
                              />
                            </div>
                          );
                        })}
                      </div>

                      {/* Unternehmensdaten Divider */}
                      <div className={`border-t pt-3 mt-2 ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                        <span className={`text-xs font-semibold tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          UNTERNEHMENSDATEN
                        </span>
                      </div>

                      {/* Name (70%) and Rechtsform (30%) - side by side */}
                      <div className="flex gap-3">
                        {['Name', 'Rechtsform'].map((label, idx) => {
                          const field = card.fields.find(f => f.label === label);
                          if (!field) return null;
                          const widthClass = idx === 0 ? 'w-[70%]' : 'w-[30%]';
                          return (
                            <div key={field.id} className={widthClass}>
                              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                {field.label}
                              </label>
                              {field.type === 'select' ? (
                                <select
                                  translate="no"                                  value={formData[field.id] || ''}
                                  onChange={(e) => onFieldChange(field.id, e.target.value)}
                                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white' : 'bg-white border-slate-300'}`}
                                >
                                  <option value="">Bitte wählen</option>
                                  {field.options?.map((option) => (
                                    <option key={option} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  translate="no"                                  value={formData[field.id] || field.defaultValue || ''}
                                  onChange={(e) => onFieldChange(field.id, e.target.value)}
                                  type={field.type}
                                  placeholder={field.placeholder}
                                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'}`}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Straße (70%) + Nr. (30%) - side by side */}
                      <div className="flex gap-3">
                        {[{ label: 'Straße', width: 'w-[70%]' }, { label: 'Nr.', width: 'w-[30%]' }].map(({ label, width }) => {
                          const field = card.fields.find(f => f.label === label);
                          if (!field) return null;
                          return (
                            <div key={field.id} className={width}>
                              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                {field.label}
                              </label>
                              <input
                                translate="no"                                value={formData[field.id] || field.defaultValue || ''}
                                onChange={(e) => onFieldChange(field.id, e.target.value)}
                                type={field.type}
                                placeholder={field.placeholder}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'}`}
                              />
                            </div>
                          );
                        })}
                      </div>

                      {/* PLZ (30%) and Ort (70%) - side by side */}
                      <div className="flex gap-3">
                        {['PLZ', 'Ort'].map((label, idx) => {
                          const field = card.fields.find(f => f.label === label);
                          if (!field) return null;
                          const widthClass = idx === 0 ? 'w-[30%]' : 'w-[70%]';
                          return (
                            <div key={field.id} className={widthClass}>
                              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                {field.label}
                              </label>
                              <input
                                translate="no"                                value={formData[field.id] || field.defaultValue || ''}
                                onChange={(e) => onFieldChange(field.id, e.target.value)}
                                type={field.type}
                                placeholder={field.placeholder}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'}`}
                              />
                            </div>
                          );
                        })}
                      </div>

                      {/* Website (50%) + Land (50%) - side by side */}
                      <div className="flex gap-3">
                        {['Website', 'Land'].map((label) => {
                          const field = card.fields.find(f => f.label === label);
                          if (!field) return null;
                          return (
                            <div key={field.id} className="w-1/2">
                              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                {field.label}
                              </label>
                              <input
                                translate="no"                                value={formData[field.id] || field.defaultValue || ''}
                                onChange={(e) => onFieldChange(field.id, e.target.value)}
                                type={field.type}
                                placeholder={field.placeholder}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : card.id === 'card-1-3' ? (
                    /* Special layout for card 1.3 Ansprechperson */
                    <div className="space-y-4">
                      {/* Anrede - full width */}
                      {card.fields.find(f => f.label === 'Anrede') && (() => {
                        const field = card.fields.find(f => f.label === 'Anrede')!;
                        return (
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                              {field.label}
                            </label>
                            {field.type === 'divider' ? (
                            <div key={field.id} className="col-span-full">
                              <div className={`border-t pt-3 mt-2 ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                                <span className={`text-xs font-semibold tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  {field.label.toUpperCase()}
                                </span>
                              </div>
                            </div>
                          ) : field.type === 'select' ? (
                              <select
                                translate="no"                                value={formData[field.id] || ''}
                                onChange={(e) => onFieldChange(field.id, e.target.value)}
                                className={`w-[45%] px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white' : 'bg-white border-slate-300'}`}
                              >
                                <option value="">Bitte wählen</option>
                                {field.options?.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                translate="no"                                value={formData[field.id] || field.defaultValue || ''}
                                onChange={(e) => onFieldChange(field.id, e.target.value)}
                                type={field.type}
                                placeholder={field.placeholder}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'}`}
                              />
                            )}
                          </div>
                        );
                      })()}
                      
                      {/* Vorname and Nachname - side by side */}
                      <div className="grid grid-cols-2 gap-3">
                        {['Vorname', 'Nachname'].map(label => {
                          const field = card.fields.find(f => f.label === label);
                          if (!field) return null;
                          return (
                            <div key={field.id}>
                              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                {field.label}
                              </label>
                              <input
                                translate="no"                                value={formData[field.id] || field.defaultValue || ''}
                                onChange={(e) => onFieldChange(field.id, e.target.value)}
                                type={field.type}
                                placeholder={field.placeholder}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Telefon and Email - side by side */}
                      <div className="grid grid-cols-2 gap-3">
                        {['Telefon', 'E-Mail'].map(label => {
                          const field = card.fields.find(f => f.label === label);
                          if (!field) return null;
                          return (
                            <div key={field.id}>
                              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                {field.label}
                              </label>
                              <input
                                translate="no"                                value={formData[field.id] || field.defaultValue || ''}
                                onChange={(e) => onFieldChange(field.id, e.target.value)}
                                type={field.type}
                                placeholder={field.placeholder}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Render any remaining fields */}
                      {card.fields.filter(f => !['Anrede', 'Vorname', 'Nachname', 'Telefon', 'E-Mail'].includes(f.label)).map(field => (
                        <div key={field.id}>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                            {field.label}
                          </label>
                          {field.type === 'divider' ? (
                            <div key={field.id} className="col-span-full">
                              <div className={`border-t pt-3 mt-2 ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                                <span className={`text-xs font-semibold tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  {field.label.toUpperCase()}
                                </span>
                              </div>
                            </div>
                          ) : field.type === 'select' ? (
                            <select
                              translate="no"                              value={formData[field.id] || ''}
                              onChange={(e) => onFieldChange(field.id, e.target.value)}
                              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white' : 'bg-white border-slate-300'}`}
                            >
                              <option value="">Bitte wählen</option>
                              {field.options?.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          ) : field.type === 'wz-search' ? (
                            <WzSearch
                              value={formData[field.id] || ''}
                              onChange={(val) => onFieldChange(field.id, val)}
                              darkMode={darkMode}
                            />
                          ) : field.type === 'date-text' ? (
                            <DateTextInput
                              value={formData[field.id] || ''}
                              onChange={(val) => onFieldChange(field.id, val)}
                              placeholder={field.placeholder}
                              darkMode={darkMode}
                            />
                          ) : field.type === 'textarea' ? (
                            <textarea
                              translate="no"                              value={formData[field.id] || ''}
                              onChange={(e) => onFieldChange(field.id, e.target.value)}
                              placeholder={field.placeholder}
                              rows={field.rows || 4}
                              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'}`}
                            />
                          ) : (
                            <input
                              translate="no"                              value={formData[field.id] || field.defaultValue || ''}
                              onChange={(e) => onFieldChange(field.id, e.target.value)}
                              type={field.type}
                              placeholder={field.placeholder}
                              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'}`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Default layout for all other cards */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-4">
                      {card.fields.map((field, index) => {
                        // Regular field rendering
                        const isFullWidth = field.fullWidth || field.type === 'textarea';
                        const gridColumnClass = isFullWidth ? 'md:col-span-2' : '';
                        
                        return (
                          <div key={field.id} className={gridColumnClass}>
                            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                              {field.label}
                            </label>
                            {field.type === 'divider' ? (
                            <div key={field.id} className="col-span-full">
                              <div className={`border-t pt-3 mt-2 ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                                <span className={`text-xs font-semibold tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  {field.label.toUpperCase()}
                                </span>
                              </div>
                            </div>
                          ) : field.type === 'select' ? (
                              <select
                                translate="no"                                value={formData[field.id] || ''}
                                onChange={(e) => onFieldChange(field.id, e.target.value)}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white' : 'bg-white border-slate-300'}`}
                              >
                                <option value="">Bitte wählen</option>
                                {field.options?.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            ) : field.type === 'wz-search' ? (
                            <WzSearch
                              value={formData[field.id] || ''}
                              onChange={(val) => onFieldChange(field.id, val)}
                              darkMode={darkMode}
                            />
                          ) : field.type === 'date-text' ? (
                              <DateTextInput
                                value={formData[field.id] || ''}
                                onChange={(val) => onFieldChange(field.id, val)}
                                placeholder={field.placeholder}
                                darkMode={darkMode}
                              />
                            ) : field.type === 'textarea' ? (
                              <textarea
                                translate="no"                                value={formData[field.id] || ''}
                                onChange={(e) => onFieldChange(field.id, e.target.value)}
                                placeholder={field.placeholder}
                                rows={field.rows || 4}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'}`}
                              />
                            ) : (
                              <input
                                translate="no"                                value={formData[field.id] || field.defaultValue || ''}
                                onChange={(e) => onFieldChange(field.id, e.target.value)}
                                type={field.type}
                                placeholder={field.placeholder}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'}`}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Berater-Liste für card-1-5 */}
                  {card.id === 'card-1-5' && consultants && onAddConsultant && onRemoveConsultant && onUpdateConsultant && (
                    <div className="mt-4">
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        Berater
                      </label>
                      <div className="space-y-2">
                        {consultants.map((consultant, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <input
                              translate="no"                              type="text"
                              value={consultant.name}
                              onChange={(e) => onUpdateConsultant(index, e.target.value)}
                              placeholder="Name des Beraters"
                              className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                                darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'
                              }`}
                            />
                            <button
                              onClick={() => onRemoveConsultant(index)}
                              disabled={consultants.length === 1}
                              className={`p-2 rounded-md border transition-colors ${
                                consultants.length === 1
                                  ? darkMode ? 'border-slate-600 text-slate-500 cursor-not-allowed' : 'border-slate-200 text-slate-300 cursor-not-allowed'
                                  : darkMode ? 'border-slate-600 text-slate-400 hover:bg-red-900/30 hover:border-red-500 hover:text-red-400' : 'border-slate-300 text-slate-600 hover:bg-red-50 hover:border-red-300 hover:text-red-600'
                              }`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={onAddConsultant}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${
                            darkMode ? 'border-slate-600 text-slate-400 hover:bg-slate-600 hover:text-slate-200' : 'border-slate-300 text-slate-600 hover:bg-slate-50 hover:text-slate-800'
                          }`}
                        >
                          <Plus className="w-3.5 h-3.5" /> Berater hinzufügen
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Beratungstage für card-1-5 */}
                  {card.id === 'card-1-5' && consultingDays && onAddConsultingDay && onRemoveConsultingDay && onUpdateConsultingDay && (
                    <div className="mt-4">
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        Beratungszeitraum
                      </label>
                      <div className="space-y-2">
                        {consultingDays.map((day, index) => (
                          <div key={index} className="flex gap-2">
                            <div className="flex-1">
                              <SharedDateTextInput
                                value={day.date}
                                onChange={(val) => onUpdateConsultingDay(index, 'date', val)}
                                darkMode={darkMode}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-sm ${
                                  dayIssueIdx.has(index)
                                    ? `focus:ring-red-500 border-red-500 ${darkMode ? 'bg-slate-600 text-white placeholder-slate-400' : 'bg-white'}`
                                    : dayConflictIdx.has(index)
                                    ? `focus:ring-amber-500 border-amber-500 ${darkMode ? 'bg-slate-600 text-white placeholder-slate-400' : 'bg-white'}`
                                    : `focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'}`
                                }`}
                              />
                            </div>
                            <div className="w-32">
                              <input
                                translate="no"                                type="number"
                                value={day.hours}
                                onChange={(e) => onUpdateConsultingDay(index, 'hours', e.target.value)}
                                placeholder="Stunden"
                                min="0"
                                step="0.5"
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'}`}
                              />
                            </div>
                            <button
                              onClick={() => onRemoveConsultingDay(index)}
                              disabled={consultingDays.length === 1}
                              className={`px-2 py-2 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                                darkMode ? 'hover:bg-slate-600 text-slate-400' : 'hover:bg-slate-100 text-slate-600'
                              }`}
                              aria-label="Tag entfernen"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={onAddConsultingDay}
                        className={`mt-3 w-full px-3 py-2 border-2 border-dashed rounded-md transition-colors flex items-center justify-center gap-2 text-sm ${
                          darkMode ? 'border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-300' : 'border-slate-300 text-slate-600 hover:border-slate-400 hover:text-slate-700'
                        }`}
                      >
                        <Plus className="w-4 h-4" />
                        Tag hinzufügen
                      </button>

                      {/* Warnung: Tage außerhalb des Beratungszeitraums */}
                      {hasWindowIssues && (
                        <div className={`mt-4 p-3 rounded-lg border text-sm ${darkMode ? 'bg-red-900/20 border-red-700/60 text-red-200' : 'bg-red-50 border-red-300 text-red-700'}`}>
                          <p className="font-medium mb-1">⚠️ Tage außerhalb des Beratungszeitraums</p>
                          <ul className="list-disc pl-5 space-y-0.5">
                            {dayWindowIssues.map((issue) => (
                              <li key={issue.i}>
                                <strong>{fmtDE(issue.date)}</strong>{' '}
                                {issue.kind === 'before'
                                  ? `liegt vor dem Beratungsbeginn (Start: ${fmtDE(winStart)})`
                                  : `liegt nach dem Beratungszeitraum (Ende: ${fmtDE(winEnd)})`}
                              </li>
                            ))}
                          </ul>
                          <p className="mt-2 text-xs opacity-90">Bitte korrigieren – die Generierung ist gesperrt, bis alle Tage im Zeitraum liegen.</p>
                        </div>
                      )}

                      {/* Hinweis: Überschneidung mit anderen Berichten (nicht blockierend) */}
                      {dayConflicts.length > 0 && (
                        <div className={`mt-4 p-3 rounded-lg border text-sm ${darkMode ? 'bg-amber-900/20 border-amber-700/60 text-amber-200' : 'bg-amber-50 border-amber-300 text-amber-800'}`}>
                          <p className="font-medium mb-1">⚠️ Mögliche Überschneidung mit anderen Berichten</p>
                          <ul className="list-disc pl-5 space-y-0.5">
                            {dayConflicts.map((conf) => (
                              <li key={conf.i}>
                                <strong>{fmtDE(conf.date)}</strong> ist bereits eingetragen in:{' '}
                                {conf.matches.map((m, k) => (
                                  <span key={k}>
                                    {k > 0 ? ', ' : ''}
                                    {m.berichtstitel}{m.kundeName ? ` (${m.kundeName})` : ''}
                                  </span>
                                ))}
                              </li>
                            ))}
                          </ul>
                          <p className="mt-2 text-xs opacity-90">Nur ein Hinweis – die Generierung bleibt möglich.</p>
                        </div>
                      )}

                      {/* Auto-calculated Beratungszeitraum */}
                      {(() => {
                        const dates = consultingDays
                          .map(d => d.date)
                          .filter(d => d)
                          .sort();
                        const totalHours = consultingDays
                          .reduce((sum, d) => sum + (parseFloat(d.hours) || 0), 0);
                        if (dates.length === 0) return null;
                        const startDate = new Date(dates[0]);
                        const endDate = new Date(dates[dates.length - 1]);
                        const formatDate = (d: Date) => d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
                        return (
                          <div className={`mt-4 p-3 rounded-lg border ${darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                            <div className="grid grid-cols-3 gap-3 text-sm">
                              <div>
                                <span className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Start</span>
                                <span className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>{formatDate(startDate)}</span>
                              </div>
                              <div>
                                <span className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Ende</span>
                                <span className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>{formatDate(endDate)}</span>
                              </div>
                              <div>
                                <span className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Gesamt</span>
                                <span className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>{totalHours} Std. / {Math.ceil(totalHours / 8)} Tage</span>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Generate Button */}
                  <div className={`pt-6 mt-5 border-t ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                    <button
                      onClick={() => onGenerateCard(card.id)}
                      disabled={hasWindowIssues}
                      title={hasWindowIssues ? 'Ein oder mehrere Beratungstage liegen außerhalb des Beratungszeitraums.' : undefined}
                      className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm font-medium shadow-sm ${
                        hasWindowIssues
                          ? 'opacity-50 cursor-not-allowed ' + (darkMode ? 'bg-slate-700 text-slate-400 border-slate-600' : 'bg-slate-100 text-slate-400 border-slate-300')
                          : (darkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50')
                      }`}
                    >
                      ✨ Text generieren
                    </button>
                  </div>
                </div>
              </div>

              {/* RIGHT: Generated Text Output */}
              <div className="flex-1 lg:w-[50%] flex flex-col">
                <div className={`rounded-lg flex-1 flex flex-col ${
                  darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50/50 border-slate-200'
                } border`}>
                  <div className="mx-5 my-5 flex-1 flex flex-col">
                    <RichTextEditor
                      content={cardState.finalText}
                      onChange={(html) => onUpdateCardText(card.id, 'finalText', html)}
                      darkMode={darkMode}
                      minHeight="200px"
                      onRefineItem={!['card-1-2', 'card-1-3', 'card-1-5'].includes(card.id) ? async (itemHtml, instruction, mode) => {
                        const result = await refineItem({ itemHtml, instruction: instruction || undefined, mode });
                        if (!result.ok) throw new Error(result.error.message);
                        return result.data.text;
                      } : undefined}
                    />
                  </div>

                </div>
              </div>
            </div>
            </div>{/* pb wrapper */}
            </div>{/* overflow-hidden */}
            </div>{/* grid collapse */}
          </div>
        );
      })}
    </div>
  );
}